'use client'

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/platform/v2/ui/dropdown-menu";

const platformLinks = [
  { to: "/platform", label: "Обзор платформы", hint: "Как устроен единый контур" },
  { to: "/platform/crm", label: "CRM и клиенты", hint: "Профиль, заявки, сделки, задачи" },
  { to: "/platform/app", label: "Приложение компании", hint: "Свой канал общения с клиентом" },
  { to: "/platform/websites", label: "Сайты", hint: "Сайт со своей админкой" },
  { to: "/platform/communications", label: "Коммуникации", hint: "Чаты, рассылки, push" },
  { to: "/platform/automation-ai", label: "Автоматизация и AI", hint: "Сценарии и AI-функции" },
  { to: "/platform/integrations", label: "Интеграции", hint: "1С, банки, внешние сервисы" },
  { to: "/platform/modules", label: "Расширения", hint: "Платные расширения под задачи" },
];

const companyLinks = [
  { to: "/platform/company", label: "О компании" },
  { to: "/platforms", label: "Платформы VCENTRE.IT" },
  { to: "/store", label: "Магазин VCENTRE" },
  { to: "/platform/achievements", label: "Достижения" },
  { to: "/platform/news", label: "Новости" },
  { to: "/platform/events", label: "Мероприятия" },
  { to: "/platform/vacancies", label: "Сотрудничество" },
  { to: "/platform/partners", label: "Партнёрам" },
  { to: "/platform/investors", label: "Инвесторам" },
  { to: "/platform/support", label: "Поддержка" },
  { to: "/platform/contacts", label: "Контакты" },
];

const solutionLinks = [
  { to: "/platform/solutions/services", label: "Услуги и сервис" },
  { to: "/platform/solutions/commerce", label: "Торговля и e-commerce" },
  { to: "/platform/solutions/b2b", label: "B2B и продажи" },
  { to: "/platform/solutions/events", label: "Мероприятия и сообщества" },
  { to: "/platform/solutions/education", label: "Образование, эксперты и подписки" },
  { to: "/platform/solutions/online-offline", label: "Онлайн + офлайн клиенты" },
];

const Header = ({ isValid }: { isValid: boolean }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenGroup(null);
  }, [pathname]);

  const navLink = "text-sm font-semibold text-foreground/85 transition-colors hover:text-primary";

  return (
    <header className="vcentre-public-portal fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-[hsl(var(--background))]/85 backdrop-blur-xl">
      <div className="container-page !max-w-[1400px] flex items-center justify-between gap-4 py-3 sm:py-4">
        <Link href="/" className="flex shrink-0 items-center" aria-label="VCENTRE.ONE — на главную">
          <img src="/assets/images/vcentreone.png" alt="VCENTRE.ONE" className="h-8 w-auto sm:h-9" />
        </Link>

        <nav className="hidden items-center gap-3 xl:flex 2xl:gap-5">
          <DropdownMenu>
            <DropdownMenuTrigger className={`flex items-center gap-1 outline-none ${navLink}`}>
              Платформа
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="vcentre-public-portal w-[340px] border-border bg-[hsl(var(--charcoal))] p-2">
              {platformLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link href={l.to} className="flex cursor-pointer flex-col items-start gap-0.5 rounded-xl px-3 py-2">
                    <span className="text-sm font-semibold text-foreground">{l.label}</span>
                    <span className="text-xs text-muted-foreground">{l.hint}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger className={`flex items-center gap-1 outline-none ${navLink}`}>
              Решения
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="vcentre-public-portal w-[340px] border-border bg-[hsl(var(--charcoal))] p-2">
              {solutionLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link href={l.to} className="flex cursor-pointer flex-col items-start gap-0.5 rounded-xl px-3 py-2">
                    <span className="text-sm font-semibold text-foreground">{l.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link href="/platform/cases" className={navLink}>
            Кейсы
          </Link>

          <Link href="/platform/pricing" className={navLink}>
            Тарифы
          </Link>

          <Link href="/platform/promotion" className={navLink}>
            Продвижение
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger className={`flex items-center gap-1 outline-none ${navLink}`}>
              Компания
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="vcentre-public-portal border-border bg-[hsl(var(--charcoal))]">
              {companyLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link href={l.to} className="cursor-pointer">
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          <Link href={isValid ? "/panel" : "/signin"} className="btn-ghost !px-5 !py-2.5 text-sm">
            Войти
          </Link>
          <Link href={isValid ? "/panel" : "/signin?source=header"} className="btn-primary whitespace-nowrap !px-5 !py-2.5 text-sm">
            Начать с VCENTRE
          </Link>
        </div>

        <button
          className="p-2 xl:hidden"
          aria-label="Меню"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className="flex h-5 w-6 flex-col justify-between">
            <span className={`h-0.5 w-full bg-foreground transition-all ${isMenuOpen ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`h-0.5 w-full bg-foreground transition-all ${isMenuOpen ? "opacity-0" : ""}`} />
            <span className={`h-0.5 w-full bg-foreground transition-all ${isMenuOpen ? "-translate-y-2 -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      {isMenuOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-border/60 bg-[hsl(var(--background))]/98 xl:hidden">
          <div className="container-page flex flex-col gap-2 py-5">
            {[
              { key: "platform", label: "Платформа", links: platformLinks },
              { key: "solutions", label: "Решения", links: solutionLinks },
              { key: "company", label: "Компания", links: companyLinks },
            ].map((group) => (
              <div key={group.key} className="border-b border-border/40 pb-2">
                <button
                  onClick={() => setOpenGroup(openGroup === group.key ? null : group.key)}
                  className="flex w-full items-center justify-between py-2 text-left text-sm font-bold text-foreground"
                >
                  {group.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === group.key ? "rotate-180" : ""}`} />
                </button>
                {openGroup === group.key && (
                  <div className="flex flex-col gap-2 border-l border-primary/40 pl-4 pb-2">
                    {group.links.map((l) => (
                      <Link key={l.to} href={l.to} className="text-sm text-muted-foreground hover:text-primary">
                        {l.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link href="/platform/pricing" className="py-2 text-sm font-bold text-foreground">
              Тарифы
            </Link>
            <Link href="/platform/cases" className="py-2 text-sm font-bold text-foreground">
              Кейсы
            </Link>
            <Link href="/platform/promotion" className="py-2 text-sm font-bold text-foreground">
              Продвижение
            </Link>

            <div className="mt-2 flex flex-col gap-3">
              <Link href={isValid ? "/panel" : "/signin?source=header"} className="btn-primary w-full">
                Начать с VCENTRE
              </Link>
              <Link href={isValid ? "/panel" : "/signin"} className="btn-ghost w-full">
                Войти
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
